// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect } from 'react';
import { Download, Mail } from 'lucide-react';
import { toast } from '../../lib/buses';
import { slugify } from '../../lib/format';
import { rowsToCsv, valueAt, downloadCsv, emailExportArtifact, exportRowsFile, askQuestion, exportQuestion } from '../../lib/backOfficeApi';
import { useAskVocabulary } from '../reports/ask/vocabCache';
import { Condition, human, kindAt, newCond, pathsOf } from '../reports/ask/questionParts';
import { Picker } from '../../ui/Picker';
import { Input, Select } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { WizardFooter } from '../../ui/Wizard';
import { windowFrom } from '../../lib/dateRange';

// ExportFlowModal — the ONE export experience (format · scope · columns · delivery).
// Pass `rows` (the in-memory dataset) and it writes exactly those; name a `table` of the ask vocabulary
// (services/ask) and every server scope is a QUESTION about that table — its rows, the page's conditions
// ({ path, op, value }), the window, the picked columns — previewed and written by the same engine as Ask,
// kept in the Export Center, emailable. Surfaces with their own server export pass `onConfirm`.
const READER_TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();
export const ExportFlowModal = ({ onClose, datasetLabel = 'records', totalRows = 0, presetColumns = [], engineDefaults = null, rows = null, table = null, filters = [], onConfirm, emailExport = null, loadAllRows = null, serverExport = false, formats: formatOptions, initialScope = null, initialFrom = '', initialTo = '', facets = [], activeFilters = null, timeWindow = null }) => {
  const source = table;   // the table every server scope asks about
  // The first format a surface offers is its default, and the shared list leads with XLSX: the file
  // is nearly always opened in a spreadsheet, and only a workbook arrives with its columns sized and
  // its phone numbers still phone numbers. CSV stays one click away for importing elsewhere.
  const [format, setFormat] = useState(() => formatOptions?.[0]?.id || 'xlsx'); // xlsx | csv | pdf | json
  // WHAT YOU ARE LOOKING AT, by default. An operator who presses Export on a filtered list wants that
  // list as a file; "every record" is the rare case, and it is reached by removing filters, not by
  // a default that silently drops them.
  const [scope, setScope] = useState(() => initialScope || ((source || loadAllRows || serverExport) ? 'filtered' : 'view')); // all | filtered | view | range

  /* THE FILE IS DESCRIBED BY THE PAGE'S OWN FILTERS — shown, and editable here.
   *
   * A surface that declares `activeFilters` ([{ id, label, value, engine: [{col,op,val}] | null,
   * params, fixed, note }]) and a `timeWindow` ({ preset: '7d'|'30d'|'90d'|'all' }) gets one scope:
   * those filters, each visible as a chip, each removable (and restorable), the time window
   * changeable. Removing them all IS "every record"; changing the window IS "narrowed to dates" —
   * so those stop being separate choices you make without seeing what they drop. A filter the
   * export cannot apply (engine: null, no params) is shown as such, never silently dropped. */
  const declared = Array.isArray(activeFilters);
  const [kept, setKept] = useState(() => new Set((activeFilters || []).map((f) => f.id)));
  const [win, setWin] = useState(() => ({ preset: timeWindow?.preset || 'all', from: timeWindow?.from || '', to: timeWindow?.to || '' }));
  // The same resolver the list uses (lib/dateRange), so "last 30 days" starts at the same instant
  // in the file as on the page.
  const winRange = (() => {
    if (!declared || !timeWindow) return {};
    if (win.preset === 'custom') return win.from && win.to ? { from: win.from, to: win.to } : {};
    const from = windowFrom(win.preset);
    return from ? { from } : {};
  })();
  const keptFilters = (activeFilters || []).filter((f) => kept.has(f.id));
  // Each kept chip is its conditions on the table ({ path, op, value }); a removed one asks nothing.
  const chipEngineFilters = keptFilters.flatMap((f) => f.engine || []);
  // THE FILE IS NAMED BY WHAT IS IN IT — the dataset, every filter it was cut by, its window. One
  // name for every export path, so no surface writes its own ("orders · every event", or nothing).
  const windowWords = !declared || !timeWindow || win.preset === 'all' ? null
    : win.preset === 'custom' ? (win.from && win.to ? `${win.from} to ${win.to}` : null)
    : `last ${win.preset}`;
  // Named by what CUTS the data: the fixed scope (the event) and every chip that narrows by a column.
  // A params-only chip (abandoned checkouts hidden) is a standing default, and "hidden" names nothing.
  const exportName = [datasetLabel, ...keptFilters.filter((f) => f.fixed || f.engine?.length).map((f) => f.value).filter(Boolean), windowWords].filter(Boolean).join(' · ');
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [columns, setColumns] = useState(() => {
    const defaults = {};
    presetColumns.forEach(c => { defaults[c.id] = c.defaultOn !== false; });
    return defaults;
  });
  const [delivery, setDelivery] = useState('download'); // download | email
  const [colsOpen, setColsOpen] = useState(false);
  const [email, setEmail] = useState('');

  // When a surface names an engine `source`, load the engine's REAL columns so the picker
  // for the server scopes (all/range) reflects exactly what the server can return — not the
  // surface's display presets, which may include computed columns the engine doesn't have.
  const [engineCols, setEngineCols] = useState(null);
  const [engineSel, setEngineSel] = useState({});

  /**
   * A STATEMENT IS AN ORDERED LIST OF NAMED COLUMNS — not a set of keys.
   *
   * `engineDefaults` may be plain keys, or `{ key, label }` pairs when the surface is producing a
   * real document. Both carry a SEQUENCE, and that sequence was being thrown away: the selection
   * was emitted as `engineCols.filter(...)`, i.e. the order the picker happens to display, which
   * is the source's declaration order. So a file whose columns a person had deliberately arranged
   * came out arranged by something else — and next month, if the source gained a column, in a
   * different order again.
   *
   * That matters because these files are opened in Excel by someone reconciling them, often
   * against a template built on last month's file. Stable, declared column positions are part of
   * the document, in the same way the header text is.
   */
  const statementShape = (Array.isArray(engineDefaults) ? engineDefaults : [])
    .map(d => (typeof d === 'string' ? { key: d, label: null } : d))
    .filter(d => d && d.key);
  const statementKeys = statementShape.map(d => d.key);
  // THE TABLE'S COLUMNS, from the vocabulary the engine answers in: its own, and each record it links to (named
  // by its title — the event, the order) with that record's columns. A surface that names its statement's
  // columns gets those ticked, in its order; otherwise the table's own columns are, and the rest wait.
  const vocab = useAskVocabulary();
  useEffect(() => {
    const T = vocab?.tables?.[source]; if (!T) return;
    const own = Object.entries(T.columns).map(([k, c]) => ({ id: k, key: k, label: c.label || human(k), kind: c.kind }));
    const linked = Object.entries(T.links || {}).flatMap(([link, to]) => {
      const tt = vocab.tables[to]; if (!tt) return [];
      return [{ id: link, key: link, label: human(link), kind: 'table' },
        ...Object.entries(tt.columns).filter(([, c]) => c.kind !== 'id').map(([k, c]) => ({ id: `${link}.${k}`, key: `${link}.${k}`, label: `${human(link)} › ${c.label || human(k)}`, kind: c.kind }))];
    });
    // A column the surface's statement names further out (an attempt's order's event) is offered too.
    const named = statementShape.filter((d) => ![...own, ...linked].some((c) => c.key === d.key)).map((d) => ({ id: d.key, key: d.key, label: d.label || human(d.key.replace(/\./g, ' ')), kind: 'text' }));
    const cols = [...own, ...linked, ...named];
    setEngineCols(cols);
    const wanted = statementKeys.length ? new Set(statementKeys) : new Set(own.filter((c) => c.kind !== 'id').map((c) => c.key));
    setEngineSel(Object.fromEntries(cols.map((c) => [c.id, wanted.has(c.key)])));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, vocab]);

  /* THE SCOPE DECIDES WHICH VOCABULARY THE PICKER SPEAKS, and that is the whole reason two
   * vocabularies exist rather than being a wart to unify away.
   *
   * A loaded row is not a database row. The orders list holds id, email, phone, totals, currency,
   * channel, rail, status, created — and does NOT hold the customer's name, the event title, the
   * payment reference or the slug, because the list never needed them to draw itself. The engine
   * holds all of those and more. So "the columns you can export" genuinely differs by scope, and
   * pretending otherwise is what produced a ticked "Payment ref" arriving blank.
   *
   * Making it structural rather than a rule people remember: EVERY server scope reads the engine's
   * columns, 'view' reads the surface's, and `confirm` below sends whichever one is active. A
   * surface can no longer hand its own spelling to the engine, because it is never the modal's
   * active vocabulary when the engine is the one running. */
  const useEngineCols = !!(engineCols && engineCols.length && scope !== 'view');
  const activeCols = useEngineCols ? engineCols : presetColumns;
  const activeSel = useEngineCols ? engineSel : columns;
  const setActiveSel = useEngineCols ? setEngineSel : setColumns;

  const toggleCol = (id) => setActiveSel(c => ({ ...c, [id]: !c[id] }));
  const selectAllCols = () => setActiveSel(Object.fromEntries(activeCols.map(c => [c.id, true])));
  const clearAllCols = () => setActiveSel(Object.fromEntries(activeCols.map(c => [c.id, false])));
  const colsOn = activeCols.filter(c => activeSel[c.id]).length;

  // Capabilities — only offer what's actually backed. Download-of-current-view is always
  // possible client-side from `rows`. "All records" needs a server export (the browser only
  // holds the loaded page); "Email a link" cannot be done client-side at all. So those two are
  // gated on a real backend handler being supplied, instead of silently degrading to the page.
  // Only count capabilities that can genuinely produce MORE than the loaded page. A generic
  // client `onConfirm` (a current-view dump) is NOT all-records and cannot email — trusting it
  // here is the silent-degradation bug. `source` (the report engine) runs the full query;
  // `loadAllRows` fetches the unfiltered set; `serverExport` flags an onConfirm that performs a
  // real server-side all-records export + email (the per-event orders path).
  const canAllRecords = !!(source || loadAllRows || serverExport);
  const canEmail = !!(emailExport || serverExport);
  // Self-correct if a caller-less mode is somehow selected.
  if (scope === 'all' && !canAllRecords) setTimeout(() => setScope('view'), 0);
  if (delivery === 'email' && !canEmail) setTimeout(() => setDelivery('download'), 0);

  /* ONLY INCLUDE — "just the paid orders", "just WhatsApp".
   *
   * The same reason the COLUMN picker speaks two vocabularies applies here, and harder: a loaded
   * row says "Paid" where the database says "successful". A chip picked in one vocabulary and sent
   * to the other matches nothing at all, silently. So each facet offers the values of whatever will
   * actually read them — the rows for 'view', the engine for every server scope — and a selection
   * is keyed by the vocabulary it was made in, so switching scope cannot carry it across.
   */
  const facetVocab = scope === 'view' ? 'rows' : 'engine';
  const [facetSel, setFacetSel] = useState({});
  const selOf = (f) => facetSel[`${facetVocab}:${f.id}`] || [];
  const toggleFacet = (f, value) => setFacetSel((s) => {
    const k = `${facetVocab}:${f.id}`;
    const cur = s[k] || [];
    return { ...s, [k]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
  });
  const clearFacet = (f) => setFacetSel((s) => ({ ...s, [`${facetVocab}:${f.id}`]: [] }));

  // Callers pass `facets={[...]}` inline — a new array every render. Keyed on its content, or the
  // fetch below would re-run on every render it causes, forever.
  const facetKey = facets.map((f) => `${f.id}:${f.rowKey}:${f.path}`).join('|');
  const rowFacetOptions = React.useMemo(() => Object.fromEntries(facets.map((f) => {
    const counts = new Map();
    for (const r of (Array.isArray(rows) ? rows : [])) {
      const v = r?.[f.rowKey];
      if (v == null || v === '' || v === '—') continue;
      counts.set(String(v), (counts.get(String(v)) || 0) + 1);
    }
    return [f.id, [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }))];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [facetKey, rows]);
  const [engineFacetOptions, setEngineFacetOptions] = useState({});
  useEffect(() => {
    if (facetVocab !== 'engine' || !source || !facets.length) return undefined;
    let alive = true;
    // A facet's values are a question too: that column of the table, counted, over the last year.
    Promise.all(facets.map((f) => askQuestion({ from: source, window: { days: 365 }, by: [f.path], measures: [{ as: 'n', agg: 'count' }], limit: 100 })
      .then((a) => [f.id, a.rows.map((r) => r[0])]).catch(() => [f.id, []])))
      .then((pairs) => { if (alive) setEngineFacetOptions(Object.fromEntries(pairs.map(([id, vals]) => [id, vals.filter((v) => v != null && v !== '').map((v) => ({ value: String(v) }))]))); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetVocab, source, facetKey]);
  // Counted exactly on the loaded rows; from the engine, listed WITHOUT counts — the only counts
  // available there are platform-wide, and one beside a status would read as this event's.
  const facetOptions = (f) => (facetVocab === 'rows' ? rowFacetOptions[f.id] : engineFacetOptions[f.id]) || [];
  // YOUR OWN FILTERS — any column of the source, any operator, its real values offered: the report
  // builder's filter rows, because an export IS a list report of its source. They exist only where
  // the engine runs (a server scope): the loaded rows on screen do not hold most of the columns.
  const [adhoc, setAdhoc] = useState([]);
  const canAdhoc = !!source && scope !== 'view';
  const complete = (c) => c.path && (c.op === 'null' || c.op === 'notnull' || (c.value != null && c.value !== '' && !(Array.isArray(c.value) && !c.value.length)));
  const adhocFilters = canAdhoc ? adhoc.filter(complete) : [];
  const facetFilters = [
    ...(facetVocab === 'engine' ? facets.filter((f) => selOf(f).length).map((f) => ({ path: f.path, op: 'in', value: selOf(f) })) : []),
    ...adhocFilters,
  ];
  const applyRowFacets = (list) => facets.reduce(
    (acc, f) => (selOf(f).length ? acc.filter((r) => selOf(f).includes(String(r?.[f.rowKey] ?? ''))) : acc),
    list,
  );
  const facetsActive = facets.some((f) => selOf(f).length);
  // A facet a lit chip already narrows would be the same fact asked twice ("Status: Paid" above,
  // "Status: successful / pending…" below) — it steps aside while that chip is on.
  const chippedCols = new Set(keptFilters.flatMap((f) => (f.engine || []).map((e) => e.path)));
  const shownFacets = facets.filter((f) => !(declared && chippedCols.has(f.path)));
  // Values in people's words: the surface's own (`words`), else the stored word made readable
  // ("box_office" → "Box office"). The stored word is what is sent.
  const wordsFor = (f, v) => f.words?.[v] || String(v).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

  // Honest size hint: the current view exports exactly the loaded rows; 'all' is unknown
  // until the server runs the job, so don't fabricate a multiplier.
  // A COMPLETE SERVER EXPORT HAS NO KNOWN ROW COUNT UNTIL IT RUNS. `totalRows` is what the page
  // has loaded; printing it beside an export that returns every matching row states a number the
  // file will not have. Only the client path (no engine `source`) genuinely exports what is loaded,
  // and only there is the count true.
  // 'view' is the only scope whose size is known before it runs — it IS the loaded rows.
  const scopeRows = scope === 'view' ? (Array.isArray(rows) ? rows.length : totalRows) : (source ? null : totalRows);
  const fileSize = scopeRows == null ? null : Math.max(8, Math.round(scopeRows * Math.max(colsOn, 1) * 0.06));

  const formats = formatOptions || [
    { id: 'xlsx', label: 'XLSX', sub: 'Opens sized to fit · phones stay intact' },
    { id: 'csv',  label: 'CSV',  sub: 'Plain text · for importing elsewhere' },
    { id: 'pdf',  label: 'PDF',  sub: 'Report layout · for sharing' },
    { id: 'json', label: 'JSON', sub: 'Raw structured data' },
  ];

  // PREVIEW = the export's own question as a list report, first rows only (a list preview reads
  // just the rows it shows). Only on request: it is a real query on the same table as the export.
  const PREVIEW_ROWS = 10;
  const canPreview = !!source && scope !== 'view';
  const [preview, setPreview] = useState({ status: 'idle', columns: [], rows: [], error: null, key: null });
  /**
   * THE FILE'S QUESTION — the table, the conditions in force, the window, the picked columns in the statement's
   * order with its headers. A window is always given unless one event is named (then that event bounds it):
   * "every record" is every record of the last 400 days, said so in the dialog — the engine reads no table whole.
   */
  const questionFor = (limit) => {
    const picked = useEngineCols ? engineCols.filter((c) => engineSel[c.id]).map((c) => c.key) : [];
    const ordered = statementKeys.length ? [...statementKeys.filter((k) => picked.includes(k)), ...picked.filter((k) => !statementKeys.includes(k))] : picked;
    const range = declared ? winRange : scope === 'range' ? { from, to } : {};
    const conds = declared ? [...chipEngineFilters, ...facetFilters] : [...(scope !== 'all' ? filters : []), ...facetFilters];
    const onOneEvent = conds.some((c) => (c.path === 'event' || c.path.endsWith('.event')) && c.op === 'eq');
    const path = timeWindow?.path ? { path: timeWindow.path } : {};
    const window = range.from ? { ...path, from: range.from, ...(range.to ? { to: range.to } : {}) } : onOneEvent ? undefined : { ...path, days: 400 };
    const labels = Object.fromEntries(statementShape.filter((d) => d.label && ordered.includes(d.key)).map((d) => [d.key, d.label]));
    return { from: source, tz: READER_TZ, ...(conds.length ? { where: conds } : {}), ...(window ? { window } : {}), columns: ordered, ...(Object.keys(labels).length ? { labels } : {}), ...(limit ? { limit } : {}) };
  };
  const previewKey = JSON.stringify(canPreview && engineCols ? questionFor(PREVIEW_ROWS) : null);
  const previewStale = preview.status === 'ready' && preview.key !== previewKey;
  const runPreview = async () => {
    const q = questionFor(PREVIEW_ROWS);
    setPreview((p) => ({ ...p, status: 'loading', error: null }));
    try {
      const a = await askQuestion(q);
      setPreview({ status: 'ready', columns: a.columns.filter((c) => !c.name.endsWith('__key')).map((c) => c.label), rows: a.rows.map((r) => r.filter((_, i) => !a.columns[i].name.endsWith('__key'))), error: null, key: JSON.stringify(q) });
    } catch (e) { setPreview({ status: 'error', columns: [], rows: [], error: e?.message || 'Preview failed', key: null }); }
  };

  const [busy, setBusy] = useState(false);
  const canExport = colsOn > 0 && (scope !== 'range' || (from && to)) && (delivery !== 'email' || /\S+@\S+/.test(email))
    && !(declared && timeWindow && win.preset === 'custom' && !(win.from && win.to));
  const exportReason = delivery === 'email' && !/\S+@\S+/.test(email) ? 'Add the email to send the link to'
    : declared && timeWindow && win.preset === 'custom' && !(win.from && win.to) ? 'Pick both custom dates'
    : scope === 'range' && !(from && to) ? 'Pick both dates of the range'
    : colsOn === 0 ? 'Choose at least one column' : null;

  // THE ROWS THIS SURFACE HANDED US, written in the browser — no server, no query. `cols` null =
  // every scalar field on the rows.
  const writeRows = async (data, cols, name) => {
    if (format === 'xlsx' || format === 'tsv') {
      // A WORKBOOK OF WHAT IS ON SCREEN, from the server's one serializer — so a 'view' export
      // comes out sized and typed exactly like an all-records one. Values travel as JSON so their
      // types survive: an amount the normaliser made a number stays summable, a phone it kept as
      // text is never turned into 2.57E+11. `—` is this UI's "no value" glyph, not data.
      const useCols = cols || [...new Set(data.flatMap(r => Object.keys(r || {})))]
        .filter(k => data.some(r => r?.[k] != null && typeof r[k] !== 'object'))
        .map(k => ({ key: k, label: k }));
      const cell = (v) => (v == null || v === '—' ? null : typeof v === 'object' ? JSON.stringify(v) : v);
      await exportRowsFile({
        name,
        format: format.toUpperCase(),
        columns: useCols.map(c => c.label),
        rows: data.map(r => useCols.map(c => cell(valueAt(r, c.key)))),
      });
    } else if (format === 'csv') {
      downloadCsv(rowsToCsv(data, cols), `${slugify(name)}.csv`);
    } else if (format === 'json') {
      const projected = cols ? data.map(r => Object.fromEntries(cols.map(c => [c.label, valueAt(r, c.key)]))) : data;
      const blob = new Blob([JSON.stringify(projected, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = `${slugify(name)}.json`; document.body.appendChild(a); a.click();
      document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else {
      // PDF still has no writer. Stay honest and keep the modal open.
      toast(`${format.toUpperCase()} export isn’t available here`, 'info', 'Use XLSX, CSV or JSON');
      return false;
    }
    return true;
  };

  // JUST THE ROWS ON SCREEN — after a search, the twelve you are looking at, straight from the page:
  // no server, no filters to read, the columns the surface shows. Offered quietly beside the real
  // export rather than as a scope you must choose between.
  const onScreen = Array.isArray(rows) ? rows.length : 0;
  const downloadOnScreen = async () => {
    const data = applyRowFacets(rows);
    if (!data.length) { toast('Nothing on screen to download', 'info', 'Every row on screen is left out by "Only include"'); return; }
    const cols = presetColumns.length ? presetColumns.filter(c => columns[c.id]).map(c => ({ key: c.key || c.id, label: c.label || c.id })) : null;
    if (await writeRows(data, cols, `${exportName} · ${data.length} on screen`)) {
      toast(`${data.length.toLocaleString()} row${data.length === 1 ? '' : 's'} downloaded`, 'success', `${format.toUpperCase()} · what was on screen`);
      onClose();
    }
  };

  const confirm = async () => {
    const label = format.toUpperCase();
    // The ACTIVE vocabulary's selection — engine keys for a server scope, the surface's own
    // field names for 'view'. Previously this always read the preset selection, which is how
    // surface spellings reached the engine and were silently dropped.
    const picked = useEngineCols
      ? engineCols.filter(c => engineSel[c.id]).map(c => c.key || c.id)
      : Object.keys(columns).filter(k => columns[k]);
    // Declared columns first, in the order the surface declared them; anything else the operator
    // ticked follows after, so a hand-picked extra is never silently dropped.
    const selectedIds = useEngineCols && statementKeys.length
      ? [...statementKeys.filter(k => picked.includes(k)), ...picked.filter(k => !statementKeys.includes(k))]
      : picked;
    const columnLabels = Object.fromEntries(
      statementShape.filter(d => d.label && selectedIds.includes(d.key)).map(d => [d.key, d.label]),
    );
    // `facetFilters`: the "Only include" choices in the engine's vocabulary, for a surface whose
    // own exporter runs on the server.
    const params = declared
      ? { format, scope: 'filtered', from: winRange.from || '', to: winRange.to || '', columns: selectedIds, columnLabels, delivery, email, facetFilters, filters: chipEngineFilters, name: exportName }
      : { format, scope, from, to, columns: selectedIds, columnLabels, delivery, email, facetFilters, name: exportName };

    // The SCOPE chooses the mechanism, not prop precedence:
    //  • 'filtered' (current view) — the in-memory `rows` already reflect the page's live
    //    filters, so the surface's own exporter (onConfirm) or the generic client path
    //    serializes exactly what's on the page: instant, exact, no round-trip.
    //  • 'all' / 'range' — the browser only holds the loaded page, so these MUST run the
    //    server engine (full query → streamed file → ExportJob in the Reports Export
    //    Center). A surface opts into that by naming an engine `source`.
    // So the server branch is tried FIRST for all/range — a surface that also passes a
    // client onConfirm (its current-view dump) doesn't let that swallow all-records.
    // With an engine `source`, the CURRENT-FILTERED view also runs the server engine WITH
    // the page's filters — so it returns the COMPLETE filtered set (the raw-list report
    // for this entity, parameterized by the table's live filters), not just the loaded page.
    // EXCEPT when the surface supplies its OWN onConfirm exporter (it already serializes the
    // exact filtered rows it holds — routing 'filtered' to the engine with empty filters
    // would wrongly export ALL records). 'all'/'range' still always run the engine.
    // 'view' NEVER goes to the server: the whole point of it is that the rows are already here.
    const needsServer = scope !== 'view' && (scope === 'all' || scope === 'range' || (scope === 'filtered' && !!source && !onConfirm));
    if (needsServer && source) {
      setBusy(true);
      try {
        // The picker for server scopes is the engine's real columns (engineCols), so the
        // selected keys map 1:1 to engine columns — true subsetting, no silent drop. If the
        // engine column list didn't load, omit columns → the engine exports the full row.
        // `selectedIds`, NOT a re-derivation from `engineCols`: that put the columns back in the
        // source's declaration order and threw away the arrangement computed above — the very
        // thing the statement shape exists to preserve.
        const { rows: n, exportId, truncated } = await exportQuestion({ question: questionFor(), name: exportName, format: label, download: delivery !== 'email' });
        if (truncated) toast('The file holds the first 10,000 rows', 'warning', 'Narrow it (dates, a status) for the rest');
        if (delivery === 'email') {
          if (!exportId) throw new Error('The export ran but returned no file to email');
          await emailExportArtifact(exportId, [email]);
        }
        const rowsWord = n != null ? `${Number(n).toLocaleString()} row${Number(n) === 1 ? '' : 's'}` : 'server export';
        toast(`${exportName} exported`, 'success', `${rowsWord} · ${label} · ${delivery === 'email' ? `emailed to ${email}` : 'downloaded'}`);
        onClose();
      } catch (e) {
        toast('Export failed', 'error', e?.message || 'Server export failed');
      } finally { setBusy(false); }
      return;
    }

    // The surface owns its export — either a real server CSV/email across scopes (orders,
    // no engine `source`) or a current-view client dump that knows its own row shape
    // (payouts/transactions).
    //
    // BUT A SERVER EXPORTER CANNOT MEAN "THE ROWS ON THIS PAGE". `serverExport` says this
    // onConfirm goes and queries the backend; the backend does not know what the operator
    // typed in the search box or which tab they are on. So handing it `filtered` produced
    // the whole dataset while the operator had asked for the five rows in front of them —
    // reported, and reproduced: every onConfirm in this codebase destructures `{ delivery,
    // email }` and drops `scope` on the floor, so the choice never reached anything.
    //
    // `filtered` therefore stays where the modal already defines it (the client path below,
    // over the `rows` this surface handed us — which ARE the filtered view, and are what the
    // row count in this dialog was counting all along). Email still goes to onConfirm: only
    // the server can send one. A bespoke client dump (no `serverExport`) is untouched.
    const onConfirmCanServeScope = !(serverExport && scope === 'view' && Array.isArray(rows) && delivery !== 'email');
    if (onConfirm && onConfirmCanServeScope) {
      setBusy(true);
      try {
        const summary = await onConfirm(params);
        const target = delivery === 'email' ? ` · emailed to ${email}` : ' · downloaded';
        toast(`${datasetLabel} exported`, 'success', summary || `${scopeRows != null ? scopeRows.toLocaleString() + ' rows' : 'export'} · ${label}${target}`);
        onClose();
      } catch (e) {
        toast('Export failed', 'error', e?.message || 'Backend rejected the export');
      } finally { setBusy(false); }
      return;
    }

    // 2) Current view (or a surface with no engine source): client-side export of the loaded rows.
    if (rows == null) { toast('Export not wired here yet', 'info', 'This surface didn’t supply its data to the exporter'); return; }
    setBusy(true);
    try {
      // Resolve scope → the actual rows. 'filtered' = what's passed (current view);
      // 'all' uses loadAllRows if the surface can fetch the unfiltered set, else falls
      // back to the filtered rows with an honest note.
      let data = Array.isArray(rows) ? rows : [];
      let scopeNote = 'current view';
      if (scope === 'all') {
        if (loadAllRows) { data = (await loadAllRows()) || []; scopeNote = 'all records'; }
        else { scopeNote = 'current view (full-dataset export needs the server export)'; }
      } else if (scope === 'range' && (from || to)) {
        scopeNote = `current view${from ? ` from ${from}` : ''}${to ? ` to ${to}` : ''}`;
      }
      // "Only include", applied to the rows in the vocabulary the chips were offered in.
      data = applyRowFacets(data);
      if (!data.length) { toast('Nothing to export', 'info', `No ${datasetLabel} in ${scopeNote}${facetsActive ? ' with those filters' : ''}`); return; }

      // Selected columns → [{key,label}] (fall back to all scalar keys when none preset).
      const cols = presetColumns.length
        ? presetColumns.filter(c => columns[c.id]).map(c => ({ key: c.key || c.id, label: c.label || c.id }))
        : null;

      if (!(await writeRows(data, cols, exportName))) return;

      if (delivery === 'email' && !emailExport) {
        toast('Downloaded instead of emailed', 'info', 'Emailing a link needs the export service — the file downloaded to you');
      } else {
        toast(`${datasetLabel} exported`, 'success', `${data.length.toLocaleString()} rows · ${label} · downloaded`);
      }
      onClose();
    } catch (e) {
      toast('Export failed', 'error', e?.message || 'Could not build the export');
    } finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} width="max-w-[640px]" title={`Export ${datasetLabel}`} sub={declared ? 'What you were looking at, as a file — change what is in it, and the columns.' : 'Choose format, scope, and the columns to include. Heavy exports are emailed.'}>
      <div className="space-y-4">
        {/* FORMAT and HOW IT ARRIVES — two either/or choices, one line. What each means is on hover. */}
        <div className="flex items-center gap-5 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280]">Format</span>
            <div className="inline-flex p-0.5 rounded-lg bg-[#F2F1ED]">
              {formats.map((f) => <button key={f.id} type="button" title={f.sub} onClick={() => setFormat(f.id)} className={`h-7 px-2.5 text-[12px] font-medium rounded-md ${format === f.id ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'}`}>{f.label}</button>)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280]">Send as</span>
            <div className="inline-flex p-0.5 rounded-lg bg-[#F2F1ED]">
              <button type="button" title="Saved by this browser — best under 5MB" onClick={() => setDelivery('download')} className={`h-7 px-2.5 text-[12px] font-medium rounded-md inline-flex items-center gap-1 ${delivery === 'download' ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'}`}><Download className="w-3.5 h-3.5" />Download</button>
              <button type="button" disabled={!canEmail} title={canEmail ? 'A link, for large files — expires in 24h' : 'Needs the server export — not available for this list yet'} onClick={() => canEmail && setDelivery('email')} className={`h-7 px-2.5 text-[12px] font-medium rounded-md inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${delivery === 'email' ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'}`}><Mail className="w-3.5 h-3.5" />Email a link</button>
            </div>
          </div>
          {delivery === 'email' && canEmail && <div className="flex-1 min-w-[180px]"><Input value={email} onChange={e => setEmail(e.target.value)} placeholder="ops@tkt.ug" type="email" /></div>}
        </div>

        {/* IN THIS FILE — the page's own filters, as chips. On by default (the file is what you were
            looking at); × removes one, a removed one stays visible and one click brings it back. */}
        {declared && (
          <div>
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">In this file</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {activeFilters.map((f) => {
                const on = kept.has(f.id);
                const applicable = !!((f.engine && f.engine.length) || f.params);
                const text = <><span className="text-[#6B7280]">{f.label}:</span> <span className="font-medium">{f.value}</span></>;
                if (f.fixed) return <span key={f.id} title={f.note || ''} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-[#F2F1ED] text-[11.5px] text-[#1F2937]">{text}</span>;
                if (!applicable) return <span key={f.id} title={f.note || 'The export cannot filter by this'} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-dashed border-[#E6E4E1] text-[11.5px] text-[#9CA3AF]">{text}<span className="ml-1.5">· not applied to the file</span></span>;
                return on ? (
                  <span key={f.id} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full border border-[#1F2937] bg-white text-[11.5px] text-[#1F2937]">
                    {text}
                    <button onClick={() => setKept((k) => { const n = new Set(k); n.delete(f.id); return n; })} aria-label={`Remove ${f.label}`} className="w-5 h-5 inline-flex items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F2F1ED] hover:text-[#1F2937]">×</button>
                  </span>
                ) : (
                  <button key={f.id} onClick={() => setKept((k) => new Set(k).add(f.id))} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-dashed border-[#D6D3D1] text-[11.5px] text-[#9CA3AF] hover:text-[#1F2937] hover:border-[#1F2937] line-through decoration-[#D6D3D1]" title="Removed — click to bring it back">
                    {text}
                  </button>
                );
              })}
              {timeWindow && (
                <span className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1.5 rounded-full border border-[#1F2937] bg-white text-[11.5px] text-[#1F2937]">
                  <span className="text-[#6B7280]">{timeWindow.label || 'Date'}:</span>
                  <Select size="inline" variant="bare" value={win.preset} onChange={(e) => setWin((w) => ({ ...w, preset: e.target.value }))} aria-label={timeWindow.label || 'Date'} className="[&>button]:font-medium [&>button]:text-[#1F2937]">
                    <option value="today">Today</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="all">All time</option>
                    <option value="custom">Custom dates…</option>
                  </Select>
                </span>
              )}
            </div>
            {declared && timeWindow && win.preset === 'custom' && (
              <div className="flex items-center gap-2 mt-2">
                <input type="date" value={win.from} onChange={(e) => setWin((w) => ({ ...w, from: e.target.value }))} className="h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded bg-white" />
                <span className="text-[10.5px] text-[#6B7280]">to</span>
                <input type="date" value={win.to} onChange={(e) => setWin((w) => ({ ...w, to: e.target.value }))} className="h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded bg-white" />
              </div>
            )}
            <div className="text-[10.5px] text-[#6B7280] mt-1.5">
              {keptFilters.some((f) => !f.fixed && ((f.engine && f.engine.length) || f.params)) || (timeWindow && win.preset !== 'all')
                ? 'Every row these describe — not just the page you can see.'
                : `Nothing narrows it — every one of the ${datasetLabel}.`}
            </div>
          </div>
        )}


        {/* Scope */}
        {!declared && (
        <div>
          <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Scope</div>
          <div className="space-y-1.5">
            <label className={`flex items-start gap-2 px-3 py-2 rounded-md border ${!canAllRecords ? 'opacity-50 cursor-not-allowed border-[#E6E4E1]' : scope === 'all' ? 'border-[#1F2937] bg-[#FBFAF7] cursor-pointer' : 'border-[#E6E4E1] hover:bg-[#FBFAF7] cursor-pointer'}`}>
              <input type="radio" checked={scope === 'all'} disabled={!canAllRecords} onChange={() => canAllRecords && setScope('all')} className="mt-0.5" />
              {/* NAMED FOR WHAT IT DISCARDS. "All records" is the phrase an operator reaches for
                  when they mean "all of the ones I filtered to", so it has to say, in the label
                  itself, that it is the option which drops what they set up. */}
              <div className="flex-1"><div className="text-[12px] font-medium text-[#1F2937]">Every record <span className="font-normal text-[#9A3412]">— ignores the filters you set</span></div><div className="text-[10.5px] text-[#6B7280] mt-0.5">{canAllRecords ? (source ? 'Unfiltered, the last 400 days · server export · emailed if large' : 'The whole book, unfiltered · server export · emailed if large') : 'Needs a server export job — not built for this list yet (only the loaded view can be exported here)'}</div></div>
            </label>
            {/* THE ROWS ALREADY ON SCREEN — instant, no round-trip, exactly what you are looking at.
                This was dropped when the server scopes arrived, as if the two were alternatives.
                They are not: one is the complete filtered set and costs a query, the other is the
                page in front of you and costs nothing. Offering the complete set is no reason to
                stop offering the quick one. Shown only where the surface actually handed us its
                rows, so it can never be a button that exports nothing. */}
            {Array.isArray(rows) && (
              <label className={`flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer ${scope === 'view' ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                <input type="radio" checked={scope === 'view'} onChange={() => setScope('view')} className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-[12px] font-medium text-[#1F2937]">Just these rows <span className="font-normal text-[#6B7280]">— {rows.length.toLocaleString()} loaded</span></div>
                  <div className="text-[10.5px] text-[#6B7280] mt-0.5">Exactly what is on the page now · instant, no server round-trip</div>
                </div>
              </label>
            )}
            <label className={`flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer ${scope === 'filtered' ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
              <input type="radio" checked={scope === 'filtered'} onChange={() => setScope('filtered')} className="mt-0.5" />
              {/* NO LOADED-ROW COUNT WHEN THIS IS A SERVER EXPORT — the count was the trap.
                  It read "Current filtered view — 25 rows" while actually exporting EVERY row
                  matching the filters, so the safe option looked like a 25-row partial and the
                  operator reasonably reached for "All records" to get "all of them" — which is
                  the one option that throws their filters away. A number that describes the page
                  cannot sit on a choice that exports the whole filtered set. */}
              <div className="flex-1"><div className="text-[12px] font-medium text-[#1F2937]">{source ? 'Everything matching your filters' : 'Current filtered view'} {!source && <span className="font-normal text-[#6B7280]">— {totalRows.toLocaleString()} rows</span>}</div><div className="text-[10.5px] text-[#6B7280] mt-0.5">{source ? 'Every matching row, not just the page you can see · server export' : 'Exactly what you see on the page now'}</div></div>
            </label>
            <label className={`flex items-start gap-2 px-3 py-2 rounded-md border ${!canAllRecords ? 'opacity-50 cursor-not-allowed border-[#E6E4E1]' : scope === 'range' ? 'border-[#1F2937] bg-[#FBFAF7] cursor-pointer' : 'border-[#E6E4E1] hover:bg-[#FBFAF7] cursor-pointer'}`}>
              <input type="radio" checked={scope === 'range'} disabled={!canAllRecords} onChange={() => canAllRecords && setScope('range')} className="mt-0.5" />
              <div className="flex-1">
                {/* A date range is a FILTER, not a third kind of completeness — and it keeps the
                    rest of them (keepFilters is `scope !== 'all'`). Saying so stops it reading as
                    "dates only, everything else dropped". */}
                <div className="text-[12px] font-medium text-[#1F2937]">Your filters, narrowed to dates <span className="font-normal text-[#6B7280]">{!canAllRecords && '— server export only'}</span></div>
                {canAllRecords && <div className="flex items-center gap-2 mt-1.5">
                  <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded bg-white" />
                  <span className="text-[10.5px] text-[#6B7280]">to</span>
                  <input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded bg-white" />
                </div>}
              </div>
            </label>
          </div>
        </div>

        )}

        {/* ONLY INCLUDE — narrow the file to the rows it is for ("just the paid orders"). Same
            visual vocabulary as the Columns picker below, so the two read as one kind of control.
            Values counted exactly when they come from the rows on screen; listed without counts
            from the engine, where the only counts available are platform-wide. */}
        {(shownFacets.length > 0 || (canAdhoc && engineCols?.length > 0)) && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280]">Only include</div>
              {(facetsActive || adhoc.length > 0) && <button onClick={() => { facets.forEach(clearFacet); setAdhoc([]); }} className="text-[10.5px] text-[#1F2937] hover:underline">Clear all</button>}
            </div>
            {/* One line per question: its name, then its values as toggles. Nothing picked = every value. */}
            <div className="space-y-1.5">
              {shownFacets.map((f) => {
                const opts = facetOptions(f);
                const sel = selOf(f);
                return (
                  <div key={f.id} className="flex items-start gap-3">
                    <div className="w-[108px] flex-shrink-0 pt-1 text-[11.5px] text-[#6B7280]">{f.label}</div>
                    <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                      {opts.length === 0 ? <span className="text-[11px] text-[#9CA3AF] pt-1">{facetVocab === 'engine' ? 'Loading…' : 'None in this view'}</span>
                        : opts.map((o) => {
                          const on = sel.includes(o.value);
                          return (
                            <button key={o.value} type="button" onClick={() => toggleFacet(f, o.value)} title={o.value}
                              className={`h-6 px-2 rounded-full border text-[11.5px] inline-flex items-center gap-1 ${on ? 'border-[#1F2937] bg-[#1F2937] text-white' : 'border-[#E6E4E1] bg-white text-[#374151] hover:border-[#C5C2BC]'}`}>
                              {wordsFor(f, o.value)}
                              {o.count != null && <span className={`tabular-nums text-[10.5px] ${on ? 'text-white/70' : 'text-[#9CA3AF]'}`}>{o.count.toLocaleString()}</span>}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
              {canAdhoc && engineCols?.length > 0 && vocab && (
                // Any condition on the table, written as Ask writes one (its values offered by asking).
                <div className={`flex flex-wrap items-center gap-1.5 text-[12px] ${shownFacets.length ? 'pt-1' : ''}`}>
                  {adhoc.map((c, i) => (
                    <Condition key={i} cond={c} paths={pathsOf(vocab, source, { includeTables: true, includeMany: true })} kind={kindAt(vocab, source, c.path)} question={{ from: source, window: { days: 365 } }}
                      onChange={(v) => setAdhoc((xs) => xs.map((x, k) => (k === i ? v : x)))} onRemove={() => setAdhoc((xs) => xs.filter((_, k) => k !== i))} />
                  ))}
                  <Picker size="sm" variant="ghost" searchable value="" placeholder={shownFacets.length ? '+ another condition' : '+ add a condition'} options={pathsOf(vocab, source, { includeTables: true, includeMany: true })} onChange={(path) => setAdhoc((xs) => [...xs, newCond(vocab, source, path)])} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Columns — driven by the engine's real columns for server scopes, the surface's
            display columns for the current view, so the picker always matches the output. */}
        {activeCols.length > 0 && (
          <div>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 text-[11.5px] text-[#374151] truncate">
                <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mr-2">Columns · {colsOn} of {activeCols.length}</span>
                {!colsOpen && <span title={activeCols.filter((c) => activeSel[c.id]).map((c) => c.label).join(', ')}>{activeCols.filter((c) => activeSel[c.id]).map((c) => c.label).slice(0, 5).join(', ')}{colsOn > 5 ? ` +${colsOn - 5}` : ''}</span>}
              </div>
              <div className="flex items-center gap-2 text-[11px] flex-shrink-0">
                {colsOpen && <><button onClick={selectAllCols} className="text-[#1F2937] hover:underline">All</button><span className="text-[#D6D3D1]">·</span><button onClick={clearAllCols} className="text-[#1F2937] hover:underline">None</button><span className="text-[#D6D3D1]">·</span></>}
                <button onClick={() => setColsOpen((o) => !o)} className="font-medium text-[#1F2937] hover:underline">{colsOpen ? 'Done' : 'Choose'}</button>
              </div>
            </div>
            {colsOpen && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {activeCols.map((c) => {
                  const on = !!activeSel[c.id];
                  return <button key={c.id} type="button" onClick={() => toggleCol(c.id)} className={`h-6 px-2 rounded-full border text-[11.5px] ${on ? 'border-[#1F2937] bg-[#1F2937] text-white' : 'border-[#E6E4E1] bg-white text-[#6B7280] hover:border-[#C5C2BC]'}`}>{c.label}</button>;
                })}
              </div>
            )}
          </div>
        )}

        {canPreview && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280]">Preview</div>
              <button type="button" onClick={runPreview} disabled={preview.status === 'loading'} className="text-[11.5px] font-medium text-[#1F2937] hover:underline disabled:opacity-50">
                {preview.status === 'loading' ? 'Reading…' : preview.status === 'idle' ? 'Show preview' : previewStale ? 'Show again — you changed it' : 'Refresh'}
              </button>
            </div>
            {preview.status === 'idle' && <div className="text-[11px] text-[#9CA3AF]">The first rows of this file, run on the server — only when you ask, since it is the same query as the export.</div>}
            {preview.status === 'error' && <div className="text-[11.5px] text-[#9B1F1F]">{preview.error}</div>}
            {preview.status === 'ready' && (
              <div className={`border border-[#E6E4E1] rounded-md overflow-auto max-h-[260px] ${previewStale ? 'opacity-50' : ''}`}>
                {preview.rows.length === 0 ? <div className="px-3 py-4 text-[12px] text-[#6B7280]">No rows match — the file would be empty.</div> : (
                  <table className="w-full text-[11px]">
                    <thead className="bg-[#FBFAF7] sticky top-0"><tr>{preview.columns.map((c) => <th key={c} className="text-left font-medium text-[#6B7280] px-2 py-1.5 whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>{preview.rows.slice(0, PREVIEW_ROWS).map((r, i) => <tr key={i} className="border-t border-[#EEEDEA]">{r.map((v, j) => <td key={j} className="px-2 py-1 whitespace-nowrap text-[#1F2937]">{v == null || v === '' ? '—' : String(v)}</td>)}</tr>)}</tbody>
                  </table>
                )}
              </div>
            )}
            {preview.status === 'ready' && preview.rows.length > 0 && <div className="text-[10.5px] text-[#9CA3AF] mt-1">First {Math.min(PREVIEW_ROWS, preview.rows.length)} rows · the file holds every matching row</div>}
          </div>
        )}

      </div>

      <WizardFooter onCancel={onClose} onNext={confirm} nextLabel={busy ? 'Exporting…' : 'Export now'} nextDisabled={!canExport || busy} nextReason={busy ? null : exportReason} primaryIcon={Download}
        note={<>
          {scopeRows == null ? `${scope === 'all' ? 'Every record' : 'Every matching row'} · ${colsOn} columns` : `${scopeRows.toLocaleString()} rows · ${colsOn} columns · ~${fileSize} KB`}
          {declared && onScreen > 0 && <> · <button type="button" onClick={downloadOnScreen} disabled={busy} className="underline decoration-dotted underline-offset-2 hover:text-[#1F2937]">or just the {onScreen.toLocaleString()} on screen</button></>}
        </>} />
    </Modal>
  );
};

// useExport — small hook that returns ({ open, node }) for any list page.
